import { makeObservable, observable } from "mobx";
import { PRICING_PLAN_FEATURES } from "components/PricingPlanField/gb/featuresData";
import type { CompanyStore } from "services/company/companyStore";
import type { FeatureStore } from "services/feature/featureStore";
import type { IssuesStore } from "services/issues/issuesStore";
import type { PricingPlanStore } from "services/pricingPlan/gb/pricingPlanStore";
import { PricingPlan } from "services/pricingPlan/pricingPlanTypes";
import { BasketQuestion } from "services/questions/gb/basketConstants";
import { QuestionsStore } from "services/questions/questionsStore";

export class BasketQuestionsStore extends QuestionsStore<BasketQuestion> {
  _voSelectionConfirmed: boolean;

  constructor(
    private readonly _companyStore: CompanyStore,
    protected readonly _issuesStore: IssuesStore,
    private readonly _pricingPlanStore: PricingPlanStore,
    private readonly _featureStore: FeatureStore,
  ) {
    super(BasketQuestion, _issuesStore);

    this._voSelectionConfirmed = !!this._companyStore.company?.useVirtualOffice;

    makeObservable(this, {
      _voSelectionConfirmed: observable,
    } as any);
  }

  markVOSelectionConfirmed(): void {
    this._voSelectionConfirmed = true;
  }

  private get _shouldShowVOSelection(): boolean {
    const company = this._companyStore.company;
    if (!company) {
      return false;
    }

    const currentPlan = this._pricingPlanStore.getPricingPlan();
    const voFeature = PRICING_PLAN_FEATURES.find((f) => f.title === "Virtual office");
    const planIncludesVO = voFeature ? (voFeature.pricingPlans as PricingPlan[]).includes(currentPlan) : false;

    return planIncludesVO && !company.useVirtualOffice && !this._voSelectionConfirmed;
  }

  _getRelevantQuestion(): BasketQuestion | undefined {
    const { company } = this._companyStore;

    if (company?.pricingPlan === PricingPlan.IntroducerFreeRegistration) {
      return BasketQuestion.Checkout;
    }
    // New-pricing experiment: land on Pricing only until a plan is picked,
    // so a refresh on checkout stays on checkout.
    if (
      this._featureStore.hasFeature("formationsNewPricingJul") &&
      (!company?.pricingPlan || company.pricingPlan === PricingPlan.Unknown)
    ) {
      return BasketQuestion.Pricing;
    }
    // User already picked a plan — don't bounce them back to Pricing on reload
    // (e.g. after the auth-token refresh that fires when the checkout mounts).
    if (company?.pricingPlan && company.pricingPlan !== PricingPlan.Unknown) {
      return this._shouldShowVOSelection ? BasketQuestion.VirtualOfficeSelection : BasketQuestion.Checkout;
    }
    return BasketQuestion.Pricing;
  }

  protected _shouldSkipQuestion(question: BasketQuestion): boolean {
    const { company } = this._companyStore;
    if (!company) {
      return true;
    }

    if (question === BasketQuestion.Pricing) {
      return company.pricingPlan === PricingPlan.IntroducerFreeRegistration;
    }

    if (question === BasketQuestion.VirtualOfficeSelection) {
      return !this._shouldShowVOSelection;
    }

    return super._shouldSkipQuestion(question);
  }
}
